import { InputAdornment, Stack } from "@mui/material";
import { SegmentedControl } from "Components/_Shared/Inputs/SegmentedControl";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import type { IncomeBulkFormType } from "Forms/Schemas/Budget/Incomes/incomeBulkFormSchema";
import { IncomeAmountAmountType, IncomeIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { type ResourcePathType, Resources, useResource } from "Infrastructure/Translations/Resources";
import type { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";

const Fields = Resources.AnalysisDetail.Budget.Incomes.Form.Fields;

const AMOUNT_MASK = [{ mask: Number, thousandsSeparator: " ", scale: 2, min: 0 }];

const AMOUNT_LABEL: Record<IncomeIncomeType, ResourcePathType> = {
  [IncomeIncomeType.employment]: Fields.PureIncome,
  [IncomeIncomeType.self_employment]: Fields.RealAverageIncome,
  [IncomeIncomeType.rental]: Fields.AverageRentalIncome,
  [IncomeIncomeType.parental]: Fields.ParentalIncome,
  [IncomeIncomeType.pension]: Fields.PensionIncome,
  [IncomeIncomeType.disability]: Fields.PensionIncome,
  [IncomeIncomeType.orphan_or_widow_pension]: Fields.PensionIncome,
  [IncomeIncomeType.other]: Fields.IncomeAmount,
};

type AmountTypeSegments = {
  readonly label: ResourcePathType;
  readonly options: { readonly value: IncomeAmountAmountType; readonly label: ResourcePathType }[];
};

const AMOUNT_TYPE_SEGMENTS: Partial<Record<IncomeIncomeType, AmountTypeSegments>> = {
  [IncomeIncomeType.disability]: {
    label: Fields.DisabilityDegree.Label,
    options: [
      { value: IncomeAmountAmountType.first_degree_disability, label: Fields.DisabilityDegree.First },
      { value: IncomeAmountAmountType.second_degree_disability, label: Fields.DisabilityDegree.Second },
      { value: IncomeAmountAmountType.third_degree_disability, label: Fields.DisabilityDegree.Third },
    ],
  },
  [IncomeIncomeType.orphan_or_widow_pension]: {
    label: Fields.PensionKind.Label,
    options: [
      { value: IncomeAmountAmountType.orphan_pension, label: Fields.PensionKind.Orphan },
      { value: IncomeAmountAmountType.widow_pension, label: Fields.PensionKind.Widow },
    ],
  },
};

type Props = {
  readonly index: number;
  readonly incomeType: IncomeIncomeType;
  readonly disabled: boolean;
};

const MonthlyAdornment = () => {
  const { t } = useResource();
  return <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>;
};

export const IncomeDraftFields: FC<Props> = ({ index, incomeType, disabled }) => {
  const { t } = useResource();
  const {
    control,
    formState: { errors },
  } = useFormContext<IncomeBulkFormType>();

  const segments = AMOUNT_TYPE_SEGMENTS[incomeType];

  return (
    <Stack spacing={Spacing.L}>
      {segments && (
        <Controller
          control={control}
          name={`drafts.${index}.amountType`}
          render={({ field }) => (
            <SegmentedControl
              name={field.name}
              label={t(segments.label)}
              value={field.value}
              options={segments.options.map((option) => ({ value: option.value, label: t(option.label) }))}
              onChange={field.onChange}
              disabled={disabled}
            />
          )}
        />
      )}

      {incomeType === IncomeIncomeType.other && (
        <BlFormInput
          control={control}
          disabled={disabled}
          name={`drafts.${index}.name`}
          label={t(Fields.IncomeName)}
          errors={errors}
          labelEndAdornment={<StyledLabelAdornment />}
          placeholder={t(Resources.AnalysisDetail.Budget.Expenses.Form.Placehodlers.OwnName)}
        />
      )}

      <BlFormInput
        control={control}
        disabled={disabled}
        name={`drafts.${index}.amountValue`}
        label={t(AMOUNT_LABEL[incomeType])}
        errors={errors}
        labelEndAdornment={<StyledLabelAdornment />}
        inputEndAdornment={<MonthlyAdornment />}
        mask={AMOUNT_MASK}
        placeholder="0"
      />
    </Stack>
  );
};
